// src/modules/operaciones/inventarios/utils/analizarMermas.js
//
// Convierte la TABLA ANCHA de mermas (una columna por bodega × tipo × cantidad/valor) en datos
// listos para el dashboard: formato largo, KPIs, agrupaciones para los gráficos, alertas de
// calidad y la tabla original para la pestaña "Datos".
//
// Este módulo NO dibuja nada ni formatea texto: solo calcula. Los componentes usan
// informesComun.js (fmtMoneda, fmtCompacto, paletas...) para mostrarlo.
//
// USO
//   const roles = detectarRoles(parseado.columnas);
//   const r = analizarMermas(parseado, roles, filtros);   // filtros es opcional
//   if (!r.esMermas) { /* usar el análisis genérico */ }
//
// CONVENCIÓN DE SIGNOS (igual que el Excel)
//   negativo = faltante / pérdida      positivo = sobrante / ganancia
//
// DEFINICIONES (coinciden con los números de referencia del PLAN_DE_VUELO, sección 4)
//   merma desconocida neta  = suma de todos los bloques "desconocida"
//   faltantes               = suma de las celdas negativas de la merma desconocida
//   sobrantes               = suma de las celdas positivas de la merma desconocida
//   merma conocida          = suma de todos los bloques "conocida" (incluye ENTRADA y SALIDA)
//   merma total neta        = desconocida neta + conocida
//   pérdida bruta           = faltantes + merma conocida   (negativa)
//   merma / venta %         = merma total neta / venta total × 100
//
// FILTROS (opcionales, tercer parámetro). Todo recalcula con los mismos datos:
//   {
//     bodegas: ["SAN JORGE"],          // claves de bodega (vacío = todas)
//     tipos: ["conocida"],             // "conocida" | "desconocida" (vacío = ambos)
//     fabricantes: ["Italcol"],        // vacío = todos
//     familias: ["CONCENTRADO"],       // vacío = todas
//     soloConMerma: false,             // solo artículos con alguna merma distinta de 0
//     ocultarSinMovimiento: false,     // oculta artículos sin venta y sin merma
//     nivelFaltantes: "celda",         // "celda" (artículo × bodega) | "articulo" (neto por artículo)
//     umbralMermaPct: 5,               // alerta si la pérdida bruta supera este % de la venta del artículo
//     incluirLargo: true,              // false = no genera el formato largo (más liviano)
//     topN: 10,                        // tamaño de los Top
//     topFamiliasDona: 6,              // familias que se muestran antes de agrupar en "Otros"
//   }
//   OJO: la venta solo existe por artículo (no por bodega). Si filtras por bodega o tipo, la merma
//   cambia pero la venta no; el % merma/venta compara la merma filtrada con la venta completa.
//
// Exporta: analizarMermas (también por defecto), TIPOS_MERMA, OPCIONES_POR_DEFECTO, SIN_FABRICANTE.

import {
  aNumero,
  normalizarTexto,
  derivarFamilia,
  FAMILIA_VACIA,
  separarFilasTotal,
} from "./detectorColumnas";

/* ========================================================================== */
/* 1. Constantes y utilidades                                                 */
/* ========================================================================== */

export const SIN_FABRICANTE = "Sin fabricante";
const SIN_UNIDAD = "Sin unidad";
const CLAVE_GENERAL = "GENERAL";

export const TIPOS_MERMA = [
  { id: "desconocida", etiqueta: "Merma desconocida" },
  { id: "conocida", etiqueta: "Merma conocida" },
];

export const OPCIONES_POR_DEFECTO = {
  bodegas: [],
  tipos: [],
  fabricantes: [],
  familias: [],
  soloConMerma: false,
  ocultarSinMovimiento: false,
  nivelFaltantes: "celda",
  umbralMermaPct: 5,
  incluirLargo: true,
  topN: 10,
  topFamiliasDona: 6,
};

// Un valor menor a medio centavo se considera cero (ruido de punto flotante).
const EPS = 0.005;

// Redondea a 2 decimales y evita "-0".
const r2 = (n) => Math.round(Number(n) * 100) / 100 || 0;

const celda = (fila, id) => (id ? fila?.[id] : undefined);
const claveMayus = (v) => normalizarTexto(v, { mayusculas: true });
const pct = (parte, total) => (total > EPS ? (parte / total) * 100 : null);

const RE_PLACEHOLDER = /^\((en blanco|blank|blanks|vacio)\)$/i;

// Texto limpio; "(en blanco)" y similares se vuelven vacío (por si el informe se guardó con un
// parser anterior).
function textoLimpio(v) {
  const t = normalizarTexto(v, { conservarTildes: true });
  return RE_PLACEHOLDER.test(normalizarTexto(t)) ? "" : t;
}

const MAPA_UNIDADES = {
  und: "UND", unid: "UND", unidad: "UND", unidades: "UND", un: "UND", uni: "UND",
  kg: "KG", kgs: "KG", kilo: "KG", kilos: "KG", kilogramo: "KG", kilogramos: "KG",
};

function normalizarUnidad(v) {
  const t = textoLimpio(v);
  if (!t) return { unidad: "", original: "" };
  const k = normalizarTexto(t, { minusculas: true }).replace(/[.\s]/g, "");
  return { unidad: MAPA_UNIDADES[k] || claveMayus(t), original: t };
}

// Devuelve siempre la primera variante vista de un texto ("ITALCOL" e "Italcol" → un solo grupo).
function crearCanon() {
  const vistos = new Map();
  return (texto) => {
    const k = claveMayus(texto);
    if (!vistos.has(k)) vistos.set(k, texto);
    return vistos.get(k);
  };
}

const conjunto = (lista) => new Set((Array.isArray(lista) ? lista : []).map(claveMayus));

function mediaYDesviacion(valores) {
  if (valores.length === 0) return { media: 0, desviacion: 0 };
  const media = valores.reduce((s, v) => s + v, 0) / valores.length;
  const varianza = valores.reduce((s, v) => s + (v - media) ** 2, 0) / valores.length;
  return { media, desviacion: Math.sqrt(varianza) };
}

const claveDeBloque = (b) => b.bodega || CLAVE_GENERAL;
const etiquetaDeBloque = (b) => b.bodegaEtiqueta || "General";

/* ========================================================================== */
/* 2. Lectura de artículos (una fila del Excel = un artículo)                 */
/* ========================================================================== */

// Cada artículo guarda `valores[k]` y `cantidades[k]` alineados con el array `bloques`.
function leerArticulos(filas, roles, bloques) {
  const canonFabricante = crearCanon();
  const colVentaValor = roles.venta?.columnaValor;
  const colVentaCantidad = roles.venta?.columnaCantidad;

  return filas.map((fila, i) => {
    const descripcion = textoLimpio(celda(fila, roles.descripcionCol));
    const item = textoLimpio(celda(fila, roles.itemCol));
    const fabTexto = textoLimpio(celda(fila, roles.fabricanteCol));
    const fabricante = fabTexto ? canonFabricante(fabTexto) : SIN_FABRICANTE;
    const { unidad, original } = normalizarUnidad(celda(fila, roles.unidadCol));
    const familia = descripcion ? derivarFamilia(descripcion) : FAMILIA_VACIA;

    const valores = new Array(bloques.length);
    const cantidades = new Array(bloques.length);
    for (let k = 0; k < bloques.length; k++) {
      valores[k] = aNumero(celda(fila, bloques[k].columnaValor));
      cantidades[k] = bloques[k].columnaCantidad ? aNumero(celda(fila, bloques[k].columnaCantidad)) : 0;
    }

    return {
      indice: i,
      fila: fila._fila ?? i + 1,
      item,
      descripcion,
      nombre: descripcion || item || `Fila ${fila._fila ?? i + 1}`,
      fabricante,
      fabricanteClave: claveMayus(fabricante),
      familia,
      familiaClave: claveMayus(familia),
      unidad,
      unidadOriginal: original,
      venta: aNumero(celda(fila, colVentaValor)),
      ventaCantidad: aNumero(celda(fila, colVentaCantidad)),
      valores,
      cantidades,
    };
  });
}

// Totales de UN artículo considerando solo los bloques indicados (`indices`).
function resumirArticulo(a, bloques, indices, nivelFaltantes) {
  let desconocida = 0;
  let conocida = 0;
  let faltantes = 0;
  let sobrantes = 0;
  let tieneMerma = false;

  for (let j = 0; j < indices.length; j++) {
    const k = indices[j];
    const v = a.valores[k];
    if (Math.abs(v) > EPS || Math.abs(a.cantidades[k]) > EPS) tieneMerma = true;
    if (bloques[k].tipo === "desconocida") {
      desconocida += v;
      if (nivelFaltantes !== "articulo") {
        if (v < 0) faltantes += v;
        else sobrantes += v;
      }
    } else {
      conocida += v;
    }
  }
  if (nivelFaltantes === "articulo") {
    faltantes = Math.min(desconocida, 0);
    sobrantes = Math.max(desconocida, 0);
  }

  const neta = desconocida + conocida;
  return {
    desconocida,
    conocida,
    neta,
    faltantes,
    sobrantes,
    perdidaBruta: faltantes + conocida,
    tieneMerma,
    sinMovimiento: !tieneMerma && Math.abs(a.venta) <= EPS && Math.abs(a.ventaCantidad) <= EPS,
  };
}

/* ========================================================================== */
/* 3. Validaciones contra las columnas TOTAL y la fila "Total general"        */
/* ========================================================================== */

function validar({ roles, bloques, articulos, filas, filaTotal }) {
  const resultados = [];
  const sumaColumna = (id) => filas.reduce((s, f) => s + aNumero(f[id]), 0);
  const sumaBloque = (k) => articulos.reduce((s, a) => s + a.valores[k], 0);
  const tolerancia = (esperado) => Math.max(1, Math.abs(esperado) * 1e-6);

  const agregar = (id, etiqueta, calculado, esperado, detalle = null, okForzado = null) => {
    const diferencia = calculado - esperado;
    const ok = okForzado !== null ? okForzado : Math.abs(diferencia) <= tolerancia(esperado);
    resultados.push({ id, etiqueta, calculado: r2(calculado), esperado: r2(esperado), diferencia: r2(diferencia), ok, detalle });
  };

  const porTipo = (tipo) =>
    bloques.reduce((s, b, k) => (tipo === "total" || b.tipo === tipo ? s + sumaBloque(k) : s), 0);

  // 1) Bloques vs columnas TOTAL MERMA del propio archivo
  const etiquetasTotal = {
    desconocida: "Merma desconocida: bloques vs columna TOTAL MERMA",
    conocida: "Merma conocida: bloques vs columna TOTAL MERMA",
    total: "Merma total: bloques vs columna TOTAL MERMA",
  };
  ["desconocida", "conocida", "total"].forEach((clave) => {
    const col = roles.totalesMerma?.[clave]?.columnaValor;
    if (col) agregar(`total-${clave}`, etiquetasTotal[clave], porTipo(clave), sumaColumna(col));
  });

  // 2) Todo vs fila "Total general"
  if (filaTotal) {
    const venta = roles.venta?.columnaValor;
    if (venta && normalizarCelda(filaTotal[venta]) !== null) {
      agregar("fila-total-venta", "Venta: suma de filas vs Total general", sumaColumna(venta), aNumero(filaTotal[venta]));
    }

    let comparados = 0;
    let calculado = 0;
    let esperado = 0;
    const fallidos = [];
    bloques.forEach((b, k) => {
      const v = normalizarCelda(filaTotal[b.columnaValor]);
      if (v === null) return;
      comparados += 1;
      const suma = sumaBloque(k);
      calculado += suma;
      esperado += v;
      if (Math.abs(suma - v) > tolerancia(v)) fallidos.push(b.etiqueta);
    });
    if (comparados > 0) {
      agregar(
        "fila-total-bloques",
        `Bloques de merma vs Total general (${comparados} bloques)`,
        calculado,
        esperado,
        fallidos.length ? `No coinciden: ${fallidos.join(", ")}` : null,
        fallidos.length === 0
      );
    }
  }
  return resultados;
}

function normalizarCelda(v) {
  if (v === null || v === undefined || String(v).trim() === "") return null;
  return aNumero(v);
}

/* ========================================================================== */
/* 4. Alertas de calidad de datos (sobre TODO el archivo, sin filtros)        */
/* ========================================================================== */

const ORDEN_NIVEL = { error: 0, advertencia: 1, info: 2, ok: 3 };

function generarAlertas({ articulos, base, roles, parseado, validaciones, filaTotal, bloquesIgnorados, umbral }) {
  const alertas = [];
  const MAX_EJEMPLOS = 8;
  const agregar = (id, nivel, titulo, detalle, cantidad = null, ejemplos = [], formato = null) =>
    alertas.push({ id, nivel, titulo, detalle, cantidad, ejemplos: ejemplos.slice(0, MAX_EJEMPLOS), formato });
  const ej = (a, valor = null) => ({ item: a.item, descripcion: a.descripcion || a.nombre, fabricante: a.fabricante, valor });

  // Validaciones
  const fallidas = validaciones.filter((v) => !v.ok);
  if (fallidas.length > 0) {
    agregar(
      "validacion",
      "error",
      "Los totales calculados no coinciden con el archivo",
      fallidas.map((v) => `${v.etiqueta}: diferencia de ${v.diferencia}`).join(" · "),
      fallidas.length
    );
  } else if (validaciones.length > 0) {
    agregar("validacion", "ok", "Totales validados", `Se verificaron ${validaciones.length} control(es) contra las columnas TOTAL y la fila Total general: todo coincide.`, validaciones.length);
  } else {
    agregar("validacion", "info", "Sin controles de validación", "El archivo no trae columnas TOTAL MERMA ni fila Total general para comparar.");
  }
  if (!filaTotal) {
    agregar("sin-fila-total", "info", "No se encontró la fila Total general", "No se pudo validar la venta ni los bloques contra un total del archivo.");
  }

  // Fabricante vacío
  const sinFab = articulos.filter((a) => a.fabricante === SIN_FABRICANTE);
  if (sinFab.length > 0) {
    agregar("fabricante-vacio", "advertencia", "Artículos sin fabricante", `${sinFab.length} artículo(s) no tienen fabricante y se agrupan como "${SIN_FABRICANTE}".`, sinFab.length, sinFab.map((a) => ej(a)));
  }

  // Unidades
  const conteoUnidades = new Map();
  articulos.forEach((a) => conteoUnidades.set(a.unidad || SIN_UNIDAD, (conteoUnidades.get(a.unidad || SIN_UNIDAD) || 0) + 1));
  const variantes = articulos.filter((a) => a.unidadOriginal && a.unidadOriginal !== a.unidad);
  if (variantes.length > 0) {
    agregar("unidades-variantes", "advertencia", "Unidades escritas de distinta forma", `${variantes.length} artículo(s) traen la unidad con variantes (UNID, UNIDAD, und...). Se agruparon como UND/KG.`, variantes.length, variantes.map((a) => ({ ...ej(a), valor: `${a.unidadOriginal} → ${a.unidad}` })));
  }
  const unidadesReales = [...conteoUnidades.keys()].filter((u) => u !== SIN_UNIDAD);
  if (unidadesReales.length > 1) {
    agregar("unidades-mezcladas", "info", "Hay varias unidades de medida", `${[...conteoUnidades.entries()].map(([u, n]) => `${u}: ${n}`).join(" · ")}. Por eso los indicadores principales van en pesos y no en unidades.`, unidadesReales.length);
  }
  if (conteoUnidades.has(SIN_UNIDAD) && roles.unidadCol) {
    agregar("unidad-vacia", "advertencia", "Artículos sin unidad de medida", `${conteoUnidades.get(SIN_UNIDAD)} artículo(s) no tienen unidad.`, conteoUnidades.get(SIN_UNIDAD));
  }

  // Sin movimiento
  const sinMov = articulos.filter((_, i) => base[i].sinMovimiento);
  if (sinMov.length > 0) {
    agregar("sin-movimiento", "info", "Artículos sin movimiento", `${sinMov.length} artículo(s) no tienen venta ni merma. Puedes ocultarlos con el filtro "Ocultar sin movimiento".`, sinMov.length, sinMov.map((a) => ej(a)));
  }

  // Pérdida alta frente a la venta
  const altos = [];
  const sinVenta = [];
  articulos.forEach((a, i) => {
    const s = base[i];
    if (s.perdidaBruta >= -EPS) return;
    if (a.venta > EPS) {
      const p = (-s.perdidaBruta / a.venta) * 100;
      if (p > umbral) altos.push({ a, p });
    } else {
      sinVenta.push({ a, perdida: s.perdidaBruta });
    }
  });
  if (altos.length > 0) {
    altos.sort((x, y) => y.p - x.p);
    agregar("merma-alta", "advertencia", `Pérdida mayor al ${umbral}% de la venta`, `${altos.length} artículo(s) pierden más del ${umbral}% de lo que venden.`, altos.length, altos.map(({ a, p }) => ej(a, r2(p))), "porcentaje");
  }
  if (sinVenta.length > 0) {
    sinVenta.sort((x, y) => x.perdida - y.perdida);
    agregar("merma-sin-venta", "advertencia", "Pérdida en artículos sin venta", `${sinVenta.length} artículo(s) tienen pérdida pero no registran venta.`, sinVenta.length, sinVenta.map(({ a, perdida }) => ej(a, r2(perdida))), "moneda");
  }

  // Ventas negativas
  const ventasNeg = articulos.filter((a) => a.venta < -EPS);
  if (ventasNeg.length > 0) {
    agregar("venta-negativa", "advertencia", "Ventas negativas", `${ventasNeg.length} artículo(s) tienen venta negativa (devoluciones o error de captura).`, ventasNeg.length, ventasNeg.map((a) => ej(a, r2(a.venta))), "moneda");
  }

  // Códigos y descripciones
  if (roles.itemCol) {
    const vistos = new Map();
    articulos.forEach((a) => {
      if (a.item) vistos.set(a.item, [...(vistos.get(a.item) || []), a]);
    });
    const repetidos = [...vistos.values()].filter((l) => l.length > 1);
    if (repetidos.length > 0) {
      agregar("item-duplicado", "advertencia", "Códigos de artículo repetidos", `${repetidos.length} código(s) aparecen más de una vez; los totales los cuentan por separado.`, repetidos.length, repetidos.map((l) => ej(l[0], `${l.length} filas`)));
    }
    const sinCodigo = articulos.filter((a) => !a.item);
    if (sinCodigo.length > 0) {
      agregar("item-vacio", "advertencia", "Artículos sin código", `${sinCodigo.length} fila(s) no tienen ITEM.`, sinCodigo.length, sinCodigo.map((a) => ej(a)));
    }
  }
  if (roles.descripcionCol) {
    const sinDesc = articulos.filter((a) => !a.descripcion);
    if (sinDesc.length > 0) {
      agregar("descripcion-vacia", "advertencia", "Artículos sin descripción", `${sinDesc.length} fila(s) no tienen descripción y quedan en la familia "${FAMILIA_VACIA}".`, sinDesc.length, sinDesc.map((a) => ej(a)));
    }
  }

  // Bloques que no se pudieron sumar
  if (bloquesIgnorados.length > 0) {
    agregar("bloques-ignorados", "info", "Bloques que no se suman", `Se omitieron: ${bloquesIgnorados.map((b) => b.etiqueta).join(", ")} (son resúmenes o no traen columna de valor).`, bloquesIgnorados.length);
  }

  // Avisos del parser y del detector
  [...(parseado.advertencias || []), ...(roles.advertencias || [])].forEach((texto, i) => {
    agregar(`aviso-${i}`, "info", "Aviso de lectura", texto);
  });

  return alertas.sort((x, y) => ORDEN_NIVEL[x.nivel] - ORDEN_NIVEL[y.nivel]);
}

/* ========================================================================== */
/* 5. Principal                                                               */
/* ========================================================================== */

function sinMatriz(motivo) {
  return { esMermas: false, motivo };
}

export function analizarMermas(parseado, roles, opciones = {}) {
  const op = { ...OPCIONES_POR_DEFECTO, ...(opciones || {}) };
  const topN = Math.max(Number(op.topN) || 10, 1);

  if (!parseado || !Array.isArray(parseado.filas) || parseado.filas.length === 0) {
    return sinMatriz("No hay filas para analizar.");
  }
  if (!roles || !roles.esMatrizMermas) {
    return sinMatriz("El archivo no tiene la estructura de una matriz de mermas por bodega.");
  }

  // Bloques que se suman: merma conocida/desconocida con columna de valor ($).
  // Los bloques "ambas" (resumen conocida-desconocida) NO se suman, para no duplicar.
  const bloques = (roles.bloques || []).filter(
    (b) => (b.tipo === "conocida" || b.tipo === "desconocida") && b.columnaValor
  );
  const bloquesIgnorados = (roles.bloques || []).filter((b) => !bloques.includes(b));
  if (bloques.length === 0) return sinMatriz("No hay bloques de merma con columna de valor.");

  // Filas de datos sin "Total general" (también funciona con informes guardados con el parser anterior)
  const separadas = separarFilasTotal(parseado.filas, roles, parseado.filaTotal);
  const filas = separadas.filas;
  const filaTotal = separadas.filaTotal;
  const otrasFilasTotales = parseado.otrasFilasTotales || separadas.otrasFilasTotales || [];

  const articulos = leerArticulos(filas, roles, bloques);

  /* ---------- Filtros ---------- */
  const filtroBodega = conjunto(op.bodegas);
  const filtroTipo = new Set((op.tipos || []).map((t) => String(t).toLowerCase()));
  const filtroFab = conjunto(op.fabricantes);
  const filtroFam = conjunto(op.familias);

  const todosIdx = bloques.map((_, k) => k);
  const activos = todosIdx.filter((k) => {
    const b = bloques[k];
    if (filtroBodega.size > 0 && !filtroBodega.has(claveMayus(claveDeBloque(b)))) return false;
    if (filtroTipo.size > 0 && !filtroTipo.has(b.tipo)) return false;
    return true;
  });
  const filtroDeBloques = activos.length !== bloques.length;

  // Resumen sin filtros (base para alertas, validación y catálogos) y resumen con filtros (vista)
  const base = articulos.map((a) => resumirArticulo(a, bloques, todosIdx, op.nivelFaltantes));
  const vista = filtroDeBloques
    ? articulos.map((a) => resumirArticulo(a, bloques, activos, op.nivelFaltantes))
    : base;

  const registros = [];
  for (let i = 0; i < articulos.length; i++) {
    const a = articulos[i];
    const s = vista[i];
    if (filtroFab.size > 0 && !filtroFab.has(a.fabricanteClave)) continue;
    if (filtroFam.size > 0 && !filtroFam.has(a.familiaClave)) continue;
    if (op.soloConMerma && !s.tieneMerma) continue;
    if (op.ocultarSinMovimiento && s.sinMovimiento) continue;
    registros.push({ a, s });
  }

  /* ---------- Formato largo + acumulación por bloque, fabricante y familia ---------- */
  const largo = [];
  const acumBloque = activos.map(() => ({ valor: 0, cantidad: 0, faltantes: 0, sobrantes: 0 }));
  const articulosPorBodega = {};
  const porFabricante = new Map();
  const porFamilia = new Map();

  const nuevoGrupo = (nombre) => ({
    nombre, venta: 0, desconocida: 0, conocida: 0, neta: 0, faltantes: 0, sobrantes: 0,
    articulos: 0, articulosConVenta: 0, articulosConMerma: 0,
  });
  const sumarGrupo = (mapa, nombre, a, s) => {
    let g = mapa.get(nombre);
    if (!g) {
      g = nuevoGrupo(nombre);
      mapa.set(nombre, g);
    }
    g.venta += a.venta;
    g.desconocida += s.desconocida;
    g.conocida += s.conocida;
    g.neta += s.neta;
    g.faltantes += s.faltantes;
    g.sobrantes += s.sobrantes;
    g.articulos += 1;
    if (Math.abs(a.venta) > EPS) g.articulosConVenta += 1;
    if (s.tieneMerma) g.articulosConMerma += 1;
  };

  const kpiAcum = { venta: 0, desconocida: 0, conocida: 0, faltantes: 0, sobrantes: 0 };
  const conteo = { total: 0, conVenta: 0, conMerma: 0, sinMovimiento: 0, mermaNegativa: 0, mermaPositiva: 0, mermaCero: 0 };

  for (const { a, s } of registros) {
    kpiAcum.venta += a.venta;
    kpiAcum.desconocida += s.desconocida;
    kpiAcum.conocida += s.conocida;
    kpiAcum.faltantes += s.faltantes;
    kpiAcum.sobrantes += s.sobrantes;

    conteo.total += 1;
    if (Math.abs(a.venta) > EPS) conteo.conVenta += 1;
    if (s.tieneMerma) conteo.conMerma += 1;
    if (s.sinMovimiento) conteo.sinMovimiento += 1;
    if (s.neta < -EPS) conteo.mermaNegativa += 1;
    else if (s.neta > EPS) conteo.mermaPositiva += 1;
    else conteo.mermaCero += 1;

    sumarGrupo(porFabricante, a.fabricante, a, s);
    sumarGrupo(porFamilia, a.familia, a, s);

    const bodegasTocadas = new Set();
    for (let j = 0; j < activos.length; j++) {
      const k = activos[j];
      const b = bloques[k];
      const v = a.valores[k];
      const c = a.cantidades[k];
      const acum = acumBloque[j];
      acum.valor += v;
      acum.cantidad += c;
      if (b.tipo === "desconocida") {
        if (v < 0) acum.faltantes += v;
        else acum.sobrantes += v;
      }
      if (Math.abs(v) <= EPS && Math.abs(c) <= EPS) continue;

      bodegasTocadas.add(claveDeBloque(b));
      if (op.incluirLargo) {
        largo.push({
          fila: a.fila,
          item: a.item,
          descripcion: a.descripcion,
          fabricante: a.fabricante,
          familia: a.familia,
          tipo: b.tipo,
          bodega: claveDeBloque(b),
          bodegaEtiqueta: etiquetaDeBloque(b),
          movimiento: b.movimiento || null,
          bloque: b.id,
          cantidad: r2(c),
          valor: r2(v),
        });
      }
    }
    bodegasTocadas.forEach((clave) => {
      articulosPorBodega[clave] = (articulosPorBodega[clave] || 0) + 1;
    });
  }

  /* ---------- KPIs ---------- */
  const ventaTotal = kpiAcum.venta;
  const mermaTotalNeta = kpiAcum.desconocida + kpiAcum.conocida;
  const perdidaBruta = kpiAcum.faltantes + kpiAcum.conocida;

  const kpis = {
    ventaTotal: r2(ventaTotal),
    mermaTotalNeta: r2(mermaTotalNeta),
    perdidaBruta: r2(perdidaBruta), // negativa
    sobrantes: r2(kpiAcum.sobrantes),
    mermaConocida: r2(kpiAcum.conocida),
    mermaDesconocidaNeta: r2(kpiAcum.desconocida),
    faltantesDesconocidos: r2(kpiAcum.faltantes),
    sobrantesDesconocidos: r2(kpiAcum.sobrantes),
    mermaVentaPct: ventaTotal > EPS ? r2((mermaTotalNeta / ventaTotal) * 100) : null, // con signo (ej. -0,11)
    perdidaBrutaPct: ventaTotal > EPS ? r2((Math.abs(perdidaBruta) / ventaTotal) * 100) : null, // magnitud (ej. 1,67)
    articulos: conteo,
  };

  // Puente (waterfall): faltantes → conocida → sobrantes → neto. `inicio` y `fin` son los extremos de cada barra.
  const pasos = [
    { id: "faltantes", etiqueta: "Faltantes (desconocida)", valor: kpis.faltantesDesconocidos },
    { id: "conocida", etiqueta: "Merma conocida", valor: kpis.mermaConocida },
    { id: "sobrantes", etiqueta: "Sobrantes (desconocida)", valor: kpis.sobrantesDesconocidos },
  ];
  let corrido = 0;
  const puente = pasos.map((p) => {
    const inicio = corrido;
    corrido += p.valor;
    return { ...p, inicio: r2(inicio), fin: r2(corrido), tipo: "delta" };
  });
  puente.push({ id: "neto", etiqueta: "Merma neta", valor: kpis.mermaTotalNeta, inicio: 0, fin: kpis.mermaTotalNeta, tipo: "total" });

  /* ---------- Por bodega ---------- */
  const mapaBodegas = new Map();
  activos.forEach((k, j) => {
    const b = bloques[k];
    const clave = claveDeBloque(b);
    let g = mapaBodegas.get(clave);
    if (!g) {
      g = {
        clave, etiqueta: etiquetaDeBloque(b),
        desconocida: 0, conocida: 0, faltantes: 0, sobrantes: 0,
        cantidadDesconocida: 0, cantidadConocida: 0,
        entrada: 0, salida: 0, cantidadEntrada: 0, cantidadSalida: 0,
        tieneEntrada: false, tieneSalida: false,
      };
      mapaBodegas.set(clave, g);
    }
    const acum = acumBloque[j];
    if (b.tipo === "desconocida") {
      g.desconocida += acum.valor;
      g.cantidadDesconocida += acum.cantidad;
      g.faltantes += acum.faltantes;
      g.sobrantes += acum.sobrantes;
    } else {
      g.conocida += acum.valor;
      g.cantidadConocida += acum.cantidad;
    }
    if (b.movimiento === "entrada") {
      g.tieneEntrada = true;
      g.entrada += acum.valor;
      g.cantidadEntrada += acum.cantidad;
    } else if (b.movimiento === "salida") {
      g.tieneSalida = true;
      g.salida += acum.valor;
      g.cantidadSalida += acum.cantidad;
    }
  });

  const porBodega = [...mapaBodegas.values()].map((g) => {
    const neta = g.desconocida + g.conocida;
    return {
      clave: g.clave,
      etiqueta: g.etiqueta,
      desconocida: r2(g.desconocida),
      conocida: r2(g.conocida),
      neta: r2(neta),
      faltantes: r2(g.faltantes),
      sobrantes: r2(g.sobrantes),
      perdidaBruta: r2(g.faltantes + g.conocida),
      cantidadDesconocida: r2(g.cantidadDesconocida),
      cantidadConocida: r2(g.cantidadConocida),
      articulosConMerma: articulosPorBodega[g.clave] || 0,
      tieneEntrada: g.tieneEntrada,
      tieneSalida: g.tieneSalida,
      entrada: g.tieneEntrada ? r2(g.entrada) : null,
      salida: g.tieneSalida ? r2(g.salida) : null,
      cantidadEntrada: g.tieneEntrada ? r2(g.cantidadEntrada) : null,
      cantidadSalida: g.tieneSalida ? r2(g.cantidadSalida) : null,
    };
  });

  const rankingBodegas = [...porBodega].sort((x, y) => x.neta - y.neta); // peor primero

  const movimientos = porBodega
    .filter((g) => g.tieneEntrada || g.tieneSalida)
    .map((g) => ({
      clave: g.clave,
      etiqueta: g.etiqueta,
      entrada: g.entrada ?? 0,
      salida: g.salida ?? 0,
      neto: r2((g.entrada ?? 0) + (g.salida ?? 0)),
      cantidadEntrada: g.cantidadEntrada ?? 0,
      cantidadSalida: g.cantidadSalida ?? 0,
    }));

  const columnasCalor = [
    { id: "desconocida", etiqueta: "Desconocida" },
    { id: "conocida", etiqueta: "Conocida" },
    { id: "entrada", etiqueta: "Entrada" },
    { id: "salida", etiqueta: "Salida" },
  ];
  const filasCalor = porBodega.map((g) => ({
    clave: g.clave,
    etiqueta: g.etiqueta,
    valores: { desconocida: g.desconocida, conocida: g.conocida, entrada: g.entrada, salida: g.salida }, // null = no aplica
  }));
  const maxAbsCalor = filasCalor.reduce(
    (m, f) => Math.max(m, ...Object.values(f.valores).map((v) => (v === null ? 0 : Math.abs(v)))),
    0
  );
  const mapaCalor = { columnas: columnasCalor, filas: filasCalor, maxAbs: maxAbsCalor };

  /* ---------- Por fabricante ---------- */
  const cerrarGrupo = (g, ventaGlobal) => ({
    nombre: g.nombre,
    venta: r2(g.venta),
    participacionVenta: ventaGlobal > EPS ? r2((g.venta / ventaGlobal) * 100) : null,
    desconocida: r2(g.desconocida),
    conocida: r2(g.conocida),
    neta: r2(g.neta),
    faltantes: r2(g.faltantes),
    sobrantes: r2(g.sobrantes),
    perdidaBruta: r2(g.faltantes + g.conocida),
    mermaPct: g.venta > EPS ? r2((g.neta / g.venta) * 100) : null, // con signo
    perdidaBrutaPct: g.venta > EPS ? r2((Math.abs(g.faltantes + g.conocida) / g.venta) * 100) : null,
    articulos: g.articulos,
    articulosConVenta: g.articulosConVenta,
    articulosConMerma: g.articulosConMerma,
  });

  const fabricantes = [...porFabricante.values()].map((g) => cerrarGrupo(g, ventaTotal)).sort((x, y) => y.venta - x.venta);
  const topFabricantesVenta = fabricantes.filter((f) => f.venta > 0).slice(0, topN);
  const topFabricantesMerma = [...fabricantes].filter((f) => f.neta < -EPS).sort((x, y) => x.neta - y.neta).slice(0, topN);
  const treemapFabricantes = fabricantes
    .filter((f) => f.perdidaBruta < -EPS)
    .map((f) => ({ name: f.nombre, size: r2(-f.perdidaBruta), venta: f.venta, perdidaBruta: f.perdidaBruta, perdidaBrutaPct: f.perdidaBrutaPct }))
    .sort((x, y) => y.size - x.size);

  /* ---------- Por familia ---------- */
  const familias = [...porFamilia.values()].map((g) => cerrarGrupo(g, ventaTotal)).sort((x, y) => y.venta - x.venta);
  const nDona = Math.max(Number(op.topFamiliasDona) || 6, 1);
  const familiasConVenta = familias.filter((f) => f.venta > 0);
  let familiasDona;
  if (familiasConVenta.length <= nDona) {
    familiasDona = familiasConVenta.map((f) => ({ name: f.nombre, value: f.venta, pct: f.participacionVenta, esOtros: false }));
  } else {
    const resto = familiasConVenta.slice(nDona);
    const ventaResto = resto.reduce((s, f) => s + f.venta, 0);
    familiasDona = [
      ...familiasConVenta.slice(0, nDona).map((f) => ({ name: f.nombre, value: f.venta, pct: f.participacionVenta, esOtros: false })),
      {
        name: "Otros",
        value: r2(ventaResto),
        pct: ventaTotal > EPS ? r2((ventaResto / ventaTotal) * 100) : null,
        esOtros: true,
        familias: resto.length,
      },
    ];
  }

  /* ---------- Por artículo ---------- */
  const listaArticulos = registros.map(({ a, s }) => ({
    id: a.indice,
    fila: a.fila,
    item: a.item,
    descripcion: a.descripcion,
    nombre: a.nombre,
    fabricante: a.fabricante,
    familia: a.familia,
    unidad: a.unidad,
    venta: r2(a.venta),
    ventaCantidad: r2(a.ventaCantidad),
    desconocida: r2(s.desconocida),
    conocida: r2(s.conocida),
    neta: r2(s.neta),
    faltantes: r2(s.faltantes),
    sobrantes: r2(s.sobrantes),
    perdidaBruta: r2(s.perdidaBruta),
    mermaPct: a.venta > EPS ? r2((s.neta / a.venta) * 100) : null,
    tieneMerma: s.tieneMerma,
    sinMovimiento: s.sinMovimiento,
  }));

  // Pareto 80/20 de la pérdida bruta (en valor absoluto). Clase A: hasta el 80 %, B: hasta el 95 %, C: el resto.
  const conPerdida = listaArticulos.filter((x) => x.perdidaBruta < -EPS).sort((x, y) => x.perdidaBruta - y.perdidaBruta);
  const totalPerdida = conPerdida.reduce((s, x) => s - x.perdidaBruta, 0);
  let acumulado = 0;
  const puntosPareto = conPerdida.map((x, i) => {
    const previo = totalPerdida > 0 ? (acumulado / totalPerdida) * 100 : 0;
    const perdida = -x.perdidaBruta;
    acumulado += perdida;
    return {
      rank: i + 1,
      item: x.item,
      nombre: x.nombre,
      fabricante: x.fabricante,
      perdida: r2(perdida),
      pct: totalPerdida > 0 ? r2((perdida / totalPerdida) * 100) : 0,
      acumulado: r2(acumulado),
      acumuladoPct: totalPerdida > 0 ? r2((acumulado / totalPerdida) * 100) : 0,
      clase: previo < 80 ? "A" : previo < 95 ? "B" : "C",
    };
  });
  const resumenClases = { A: { articulos: 0, perdida: 0, pct: 0 }, B: { articulos: 0, perdida: 0, pct: 0 }, C: { articulos: 0, perdida: 0, pct: 0 } };
  puntosPareto.forEach((p) => {
    resumenClases[p.clase].articulos += 1;
    resumenClases[p.clase].perdida += p.perdida;
  });
  Object.values(resumenClases).forEach((c) => {
    c.perdida = r2(c.perdida);
    c.pct = totalPerdida > 0 ? r2((c.perdida / totalPerdida) * 100) : 0;
  });
  const indice80 = puntosPareto.findIndex((p) => p.acumuladoPct >= 80);
  const pareto = {
    totalPerdida: r2(totalPerdida),
    articulos: puntosPareto.length,
    articulosPara80: indice80 >= 0 ? indice80 + 1 : puntosPareto.length,
    clases: resumenClases,
    puntos: puntosPareto,
  };

  // Top artículos
  const topPerdida = conPerdida.slice(0, topN);
  const topSobrante = listaArticulos.filter((x) => x.sobrantes > EPS).sort((x, y) => y.sobrantes - x.sobrantes).slice(0, topN);

  // Dispersión venta vs merma + atípicos (|z| > 3 sobre la merma neta de los artículos con merma)
  const { media, desviacion } = mediaYDesviacion(listaArticulos.filter((x) => x.tieneMerma).map((x) => x.neta));
  const dispersion = listaArticulos
    .filter((x) => x.tieneMerma || Math.abs(x.venta) > EPS)
    .map((x) => {
      const z = x.tieneMerma && desviacion > 0 ? (x.neta - media) / desviacion : 0;
      return {
        item: x.item, nombre: x.nombre, fabricante: x.fabricante, familia: x.familia,
        venta: x.venta, merma: x.neta, perdidaBruta: x.perdidaBruta, z: r2(z), atipico: Math.abs(z) > 3,
      };
    });
  const atipicos = dispersion.filter((d) => d.atipico).sort((x, y) => Math.abs(y.z) - Math.abs(x.z)).slice(0, topN);

  /* ---------- Validaciones y alertas (siempre sobre el archivo completo) ---------- */
  const validaciones = validar({ roles, bloques, articulos, filas, filaTotal });
  const alertas = generarAlertas({
    articulos, base, roles, parseado, validaciones, filaTotal, bloquesIgnorados, umbral: Number(op.umbralMermaPct) || 5,
  });

  /* ---------- Tabla original para la pestaña "Datos" ---------- */
  const idsVisibles = new Set(registros.map(({ a }) => a.indice));
  const filasVisibles = filas.filter((_, i) => idsVisibles.has(i));

  const columnasPorBodega = new Map();
  (roles.bloques || []).forEach((b) => {
    if (!b.bodega) return;
    const lista = columnasPorBodega.get(b.bodega) || { clave: b.bodega, etiqueta: etiquetaDeBloque(b), columnas: [] };
    [b.columnaCantidad, b.columnaValor].forEach((id) => id && lista.columnas.push(id));
    columnasPorBodega.set(b.bodega, lista);
  });
  const idsFijos = [roles.itemCol, roles.descripcionCol, roles.unidadCol, roles.fabricanteCol].filter(Boolean);
  const idsTotales = Object.values(roles.totalesMerma || {}).flatMap((t) => (t ? [t.columnaCantidad, t.columnaValor].filter(Boolean) : []));
  const idsVenta = roles.venta ? [roles.venta.columnaCantidad, roles.venta.columnaValor].filter(Boolean) : [];
  const grupos = [
    { id: "identificacion", etiqueta: "Artículo", columnas: idsFijos },
    ...[...columnasPorBodega.values()].map((g) => ({ id: `bodega:${g.clave}`, etiqueta: g.etiqueta, columnas: g.columnas })),
    { id: "total-merma", etiqueta: "Total merma", columnas: idsTotales },
    { id: "venta", etiqueta: "Venta", columnas: idsVenta },
  ].filter((g) => g.columnas.length > 0);
  const agrupadas = new Set(grupos.flatMap((g) => g.columnas));
  const otras = (parseado.columnas || []).map((c) => c.id).filter((id) => !agrupadas.has(id));
  if (otras.length > 0) grupos.push({ id: "otras", etiqueta: "Otras columnas", columnas: otras });

  /* ---------- Catálogos para la barra de filtros (siempre completos) ---------- */
  const ventaPorFab = new Map();
  const ventaPorFam = new Map();
  articulos.forEach((a) => {
    ventaPorFab.set(a.fabricante, (ventaPorFab.get(a.fabricante) || 0) + a.venta);
    ventaPorFam.set(a.familia, (ventaPorFam.get(a.familia) || 0) + a.venta);
  });
  const ordenarPorVenta = (mapa) => [...mapa.entries()].sort((x, y) => y[1] - x[1]).map(([nombre]) => nombre);
  const catalogos = {
    bodegas: (roles.bodegas || []).map((b) => ({ clave: b.clave, etiqueta: b.etiqueta })),
    tipos: TIPOS_MERMA,
    fabricantes: ordenarPorVenta(ventaPorFab),
    familias: ordenarPorVenta(ventaPorFam),
  };

  const filtrosActivos = [];
  if (filtroBodega.size > 0) filtrosActivos.push("bodegas");
  if (filtroTipo.size > 0) filtrosActivos.push("tipos");
  if (filtroFab.size > 0) filtrosActivos.push("fabricantes");
  if (filtroFam.size > 0) filtrosActivos.push("familias");
  if (op.soloConMerma) filtrosActivos.push("soloConMerma");
  if (op.ocultarSinMovimiento) filtrosActivos.push("ocultarSinMovimiento");

  return {
    esMermas: true,
    meta: {
      totalArticulos: articulos.length,
      articulosVisibles: registros.length,
      bloquesUsados: bloques.map((b) => b.id),
      bloquesIgnorados: bloquesIgnorados.map((b) => b.id),
      nivelFaltantes: op.nivelFaltantes,
      filtrosActivos,
      // La venta no se puede separar por bodega: con filtro de bodega/tipo el % merma/venta es aproximado.
      ventaNoFiltrable: filtroDeBloques,
      tieneFilaTotal: Boolean(filaTotal),
    },
    kpis,
    puente,
    bodegas: { lista: porBodega, ranking: rankingBodegas, mapaCalor, movimientos },
    fabricantes: { lista: fabricantes, topVenta: topFabricantesVenta, topMerma: topFabricantesMerma, treemap: treemapFabricantes },
    familias: { lista: familias, dona: familiasDona },
    articulos: { lista: listaArticulos, pareto, topPerdida, topSobrante, dispersion, atipicos },
    largo,
    validaciones,
    alertas,
    catalogos,
    datos: {
      columnas: parseado.columnas || [],
      filas: filasVisibles,
      filaTotal,
      otrasFilasTotales,
      grupos,
      totalFilas: filas.length,
    },
  };
}

export default analizarMermas;